// src/config/environment.js
// Environment-based configuration for development and production
class EnvironmentConfig {
  constructor() {
    this.hostname = window.location.hostname;
    this.protocol = window.location.protocol;
    this.port = window.location.port;
  }

  get isDevelopment() {
    return this.hostname === 'localhost' || this.hostname === '127.0.0.1';
  }

  get isProduction() {
    return this.hostname === 'clarity-aa6d1.web.app' || this.hostname === 'advicelink.org' || this.hostname === 'advicelink-production.up.railway.app';
  }

  get backendUrl() {
    if (this.isDevelopment) {
      return 'http://localhost:3001';
    } else {
      // Use Railway subdomain for production backend API
      return 'https://advicelink-production.up.railway.app';
    }
  }

  get frontendUrl() {
    if (this.isDevelopment) {
      return 'http://localhost:3000';
    } else {
      return 'https://clarity-aa6d1.web.app';
    }
  }

  get apiEndpoints() {
    const base = this.backendUrl;
    return {
      base: base,
      claude: `${base}/api/claude`,
      generateSoa: `${base}/api/generate-soa`,
      downloadSoa: `${base}/api/download-soa`,
      emailSoa: `${base}/api/email-soa`,
      sendEmail: `${base}/api/send-email`,
      helpDesk: `${base}/api/support/help-desk`,
      contact: `${base}/api/contact`,
      supportChatNotify: `${base}/api/support/chat/notify`,
      supportChatAttachments: `${base}/api/support/chat/attachments`,
      supportChatAttachment: (threadEmail, fileId) => `${base}/api/support/chat/attachments/${encodeURIComponent(threadEmail)}/${encodeURIComponent(fileId)}`,
      excelData: `${base}/api/excel-data`,
      excelDataClient: (clientId) => `${base}/api/excel-data/client/${clientId}`,
      excelDataDownload: (clientId) => `${base}/api/excel-data/client/${clientId}/download`,
      generateOccupations: `${base}/api/generate-occupations`,
      omnilifeQuote: `${base}/api/omnilife/quote`,
      omnilifeAvailability: `${base}/api/omnilife/availability`,
      omnilifeOccupationMappings: (occupationId) => `${base}/api/omnilife/occupations/${occupationId}/mappings`,
      omnilifeSupplierLink: (portfolioCode) => `${base}/api/omnilife/quote/portfolio/${portfolioCode}/supplierLink`,
      omnilifeQuoteValidation: (portfolioCode) => `${base}/api/omnilife/quote/portfolio/${portfolioCode}/quoteValidation`,
      omnilifeQuoteValidationReport: (portfolioCode) => `${base}/api/omnilife/quote/portfolio/${portfolioCode}/quoteValidationReport`,
      placesAutocomplete: `${base}/api/places/autocomplete`,
      placesDetails: (placeId) => `${base}/api/places/${encodeURIComponent(placeId)}/details`,
      soaDocxNative: (clientId) => `${base}/api/clients/${clientId}/soa-docx-native`,
      roaEoDocxNative: (clientId) => `${base}/api/clients/${clientId}/roa-eo-docx-native`,
      arSoaDocxNative: (clientId) => `${base}/api/clients/${clientId}/ar-soa-docx-native`,
      arRoaDocxNative: (clientId) => `${base}/api/clients/${clientId}/ar-roa-docx-native`,
      /** @deprecated use arRoaDocxNative */
      arLetterDocxNative: (clientId) => `${base}/api/clients/${clientId}/ar-roa-docx-native`,
      projectionComparisonDocx: (clientId) => `${base}/api/clients/${clientId}/projection-comparison-docx`,
      version: `${base}/api/version`,
      adminDeploymentUpdates: `${base}/api/admin/deployment-updates`,
      adminDeploymentUpdatesEnsure: `${base}/api/admin/deployment-updates/ensure`,
      createAuthUser: `${base}/api/admin/create-auth-user`,
      sendPasswordReset: `${base}/api/admin/send-password-reset`,
      forgotPassword: `${base}/api/auth/forgot-password`,
      microsoftCalendarStart: `${base}/api/auth/microsoft/calendar/start`,
      microsoftCalendarAdminConsent: `${base}/api/auth/microsoft/admin-consent`,
      microsoftCalendarDisconnect: `${base}/api/auth/microsoft/calendar/disconnect`,
      microsoftCalendarClearPending: `${base}/api/auth/microsoft/calendar/clear-pending`,
      microsoftMailStart: `${base}/api/auth/microsoft/mail/start`,
      microsoftMailAdminConsent: `${base}/api/auth/microsoft/mail/admin-consent`,
      microsoftMailDisconnect: `${base}/api/auth/microsoft/mail/disconnect`,
      microsoftMailClearPending: `${base}/api/auth/microsoft/mail/clear-pending`,
      mailStatus: `${base}/api/mail/status`,
      mailMailbox: (mailboxKey, path = '') => `${base}/api/mail/mailboxes/${encodeURIComponent(mailboxKey)}${path}`,
      mailSignature: `${base}/api/mail/signature`,
      mailTemplates: `${base}/api/mail/templates`,
      clientMail: (clientId, path = '') => `${base}/api/clients/${clientId}/mail${path}`,
      projectionsMultiSeries: `${base}/api/projections/multi-series`,
      projectionPreview: (clientId) => `${base}/api/clients/${clientId}/projection-preview`,
      contributionProjectionComparison: (clientId) => `${base}/api/clients/${clientId}/contribution-projection-comparison`,
      ttrProjectionComparison: (clientId) => `${base}/api/clients/${clientId}/ttr-projection-comparison`,
      debtProjectionComparison: (clientId) => `${base}/api/clients/${clientId}/debt-projection-comparison`,
      documentPreview: (clientId) => `${base}/api/clients/${encodeURIComponent(clientId)}/document-preview`,
      documentPreviewAi: (clientId) => `${base}/api/clients/${encodeURIComponent(clientId)}/document-preview/ai`,
      // Video guides: signed links to the recordings in Storage.
      guideVideoUrls: `${base}/api/guide-videos/urls`,
      // The few the public landing page plays, without sign-in.
      publicGuideVideo: (id) => `${base}/api/public/guide-videos/${encodeURIComponent(id)}`,
      adviceScenarios: (clientId) => `${base}/api/clients/${encodeURIComponent(clientId)}/advice-scenarios`,
      aboutYouObservations: `${base}/api/soa/about-you-observations`,
      calendarAvailability: `${base}/api/calendar/availability`,
      calendarEvents: `${base}/api/calendar/events`,
      calendarEventsForAdviser: `${base}/api/calendar/events-for-adviser`,
      bookWelcomeCall: `${base}/api/calendar/book-welcome-call`,
      bookAR: `${base}/api/calendar/book-ar`,
      bookInsuranceApplication: `${base}/api/calendar/book-insurance-application`,
      cancelCalendarEvent: `${base}/api/calendar/cancel-event`,
      moveCalendarEvent: `${base}/api/calendar/move-event`,
      createCalendarEvent: `${base}/api/calendar/create-event`,
      updateCalendarEvent: `${base}/api/calendar/update-event`,
      updateWelcomeCallStatus: `${base}/api/calendar/welcome-call-status`,
      updateARStatus: `${base}/api/calendar/ar-status`,
      insuranceSetting: (filename) => `${base}/api/insurance-settings/${filename}`,
      adminTeams: (teamType) => `${base}/api/admin/teams/${teamType}`,
      adminDisconnectUserCalendar: `${base}/api/admin/users/disconnect-calendar`,
      adminDisconnectUserMail: `${base}/api/admin/users/disconnect-mail`,
      adminTeam: (teamType, teamId) => `${base}/api/admin/teams/${teamType}/${teamId}`,
      teamRosters: `${base}/api/team-rosters`,
      adminPortfolios: (portfolioType) => `${base}/api/admin/portfolios/${portfolioType}`,
      adminPortfolio: (portfolioType, portfolioId) => `${base}/api/admin/portfolios/${portfolioType}/${portfolioId}`,
      adminPortfolioPeriod: (portfolioType, portfolioId, periodKey) =>
        `${base}/api/admin/portfolios/${portfolioType}/${portfolioId}/periods/${periodKey}`,
      adminPortfoliosCatalog: `${base}/api/admin/portfolios-catalog`,
      adminPortfolioPlatforms: `${base}/api/admin/portfolio-platforms`,
      adminPortfolioFirmDefaults: `${base}/api/admin/portfolio-platforms/firm-defaults`,
      adminPortfolioPlatform: (slug) => `${base}/api/admin/portfolio-platforms/${slug}`,
      adminPortfolioPlatformReferences: (slug) => `${base}/api/admin/portfolio-platforms/${slug}/references`,
      adminPortfolioReferences: (portfolioType, portfolioId) =>
        `${base}/api/admin/portfolios/${portfolioType}/${portfolioId}/references`,
      adminPortfoliosSync: `${base}/api/admin/portfolios/sync`,
      adminPortfoliosSyncFolders: `${base}/api/admin/portfolios/sync-folders`,
      adminL4lPortfolios: `${base}/api/admin/l4l-portfolios`,
      // Guided Advice fund research waiting for an admin (Admin → Portfolios → Industry Funds).
      adminFundDocumentReviews: `${base}/api/admin/fund-document-reviews`,
      adminL4lFundReferences: `${base}/api/admin/l4l-portfolios/fund-references`,
      adminL4lFundArchive: `${base}/api/admin/l4l-portfolios/fund-archive`,
      adminL4lFund: `${base}/api/admin/l4l-portfolios/fund`,
      adminL4lPortfolio: (portfolioId) => `${base}/api/admin/l4l-portfolios/${portfolioId}`,
      adminL4lPortfoliosSync: `${base}/api/admin/l4l-portfolios/sync`,
      adminL4lPortfoliosSyncFolders: `${base}/api/admin/l4l-portfolios/sync-folders`,
      clientRecord: (clientId) => `${base}/api/clients/${clientId}`,
      // Workflows (Adviser Logic-style templates + runs)
      adminWorkflowTemplates: `${base}/api/admin/workflow-templates`,
      adminCorrespondence: `${base}/api/admin/correspondence`,
      adminCorrespondenceRecipients: `${base}/api/admin/correspondence/recipients`,
      adminCorrespondenceWorkflowWording: `${base}/api/admin/correspondence/workflow-wording`,
      adminWorkflowTemplate: (templateId) => `${base}/api/admin/workflow-templates/${encodeURIComponent(templateId)}`,
      adminWorkflowTemplateArchive: (templateId) => `${base}/api/admin/workflow-templates/${encodeURIComponent(templateId)}/archive`,
      adminWorkflowTemplateCopy: (templateId) => `${base}/api/admin/workflow-templates/${encodeURIComponent(templateId)}/copy`,
      adminWorkflowTemplateDelete: (templateId) => `${base}/api/admin/workflow-templates/${encodeURIComponent(templateId)}`,
      adminWorkflowTemplateValidate: `${base}/api/admin/workflow-templates/validate`,
      adminWorkflowTemplateRunCounts: `${base}/api/admin/workflow-templates/run-counts`,
      adminWorkflowMapLayout: `${base}/api/admin/workflow-templates/map-layout`,
      adminWorkflowMapColours: `${base}/api/admin/workflow-templates/map-colours`,
      adminWorkflowFirmRoles: `${base}/api/admin/workflow-templates/firm-roles`,
      adminWorkflowMeetingTypes: `${base}/api/admin/workflow-templates/meeting-types`,
      clientMeetingTypes: (clientId) => `${base}/api/clients/${clientId}/meeting-types`,
      clientBookableCalendars: (clientId, taskId) => `${base}/api/clients/${clientId}/bookable-calendars${taskId ? `?taskId=${encodeURIComponent(taskId)}` : ''}`,
      clientWorkflowTaskBooking: (clientId, taskId) => `${base}/api/clients/${clientId}/workflow-tasks/${encodeURIComponent(taskId)}/booking`,
      clientWorkflowTemplates: (clientId) => `${base}/api/clients/${clientId}/workflow-templates`,
      clientWorkflowMapLayout: (clientId) => `${base}/api/clients/${clientId}/workflow-map-layout`,
      clientWorkflowRoles: (clientId) => `${base}/api/clients/${clientId}/workflow-roles`,
      clientWorkflowRunAdHoc: (clientId) => `${base}/api/clients/${clientId}/workflow-runs/ad-hoc`,
      clientWorkflowRuns: (clientId) => `${base}/api/clients/${clientId}/workflow-runs`,
      clientWorkflowRunsAutoStart: (clientId) => `${base}/api/clients/${clientId}/workflow-runs/auto-start`,
      clientWorkflowRunCancel: (clientId, runId) => `${base}/api/clients/${clientId}/workflow-runs/${encodeURIComponent(runId)}/cancel`,
      clientWorkflowRun: (clientId, runId) => `${base}/api/clients/${clientId}/workflow-runs/${encodeURIComponent(runId)}`,
      clientWorkflowTask: (clientId, taskId) => `${base}/api/clients/${clientId}/workflow-tasks/${encodeURIComponent(taskId)}`,
      clientWorkflowFactFindComplete: (clientId) => `${base}/api/clients/${clientId}/workflow-tasks/fact-find/complete`,
      clientWorkflowFactFindResubmit: (clientId) => `${base}/api/clients/${clientId}/workflow-tasks/fact-find/resubmit`,
      clientWorkflowWelcomeCallBooked: (clientId) => `${base}/api/clients/${clientId}/workflow-tasks/welcome-call-booked`,
      clientWorkflowWelcomeCallDueDate: (clientId) => `${base}/api/clients/${clientId}/workflow-tasks/welcome-call-due-date`,
      clientWorkflowTaskAction: (clientId, taskId, action) => `${base}/api/clients/${clientId}/workflow-tasks/${encodeURIComponent(taskId)}/${action}`,
      clientWorkflowTaskAssignAdviser: (clientId, taskId) => `${base}/api/clients/${clientId}/workflow-tasks/${encodeURIComponent(taskId)}/assign-adviser`,
      clientAdviser: (clientId) => `${base}/api/clients/${clientId}/adviser`,
      // Guided Advice (Oct 2026): 'pre-answers' | 'turn' | 'explanations' | 'file-check' | 'audit' (classify-evidence posts through documentExtractionUpload).
      clientGuidedAdvice: (clientId, action) => `${base}/api/clients/${clientId}/guided-advice/${action}`,
      // Para on the client file (Oct 2026): whether the floating button shows, and its chat.
      paraClientFile: `${base}/api/para/client-file`,
      paraOpens: (clientId) => `${base}/api/para/opens/${encodeURIComponent(clientId)}`,
      clientParaTurn: (clientId) => `${base}/api/clients/${clientId}/para/turn`,
      clientParaFileActions: (clientId) => `${base}/api/clients/${clientId}/para/file-actions`,
      clientWorkflowTaskOutcome: (clientId, taskId) => `${base}/api/clients/${clientId}/workflow-tasks/${encodeURIComponent(taskId)}/outcome`,
      clientWorkflowTaskNotes: (clientId, taskId) => `${base}/api/clients/${clientId}/workflow-tasks/${encodeURIComponent(taskId)}/notes`,
      clientWorkflowTaskCompletionEmail: (clientId, taskId) => `${base}/api/clients/${clientId}/workflow-tasks/${encodeURIComponent(taskId)}/completion-email`,
      clientDuplicate: (clientId) => `${base}/api/clients/${clientId}/duplicate`,
      clientDocuments: (clientId) => `${base}/api/clients/${clientId}/documents`,
      clientSendFsgEmail: (clientId) => `${base}/api/clients/${clientId}/send-fsg-email`,
      clientDocumentsGenerate: (clientId) => `${base}/api/clients/${clientId}/documents/generate`,
      clientDocument: (clientId, docId) => `${base}/api/clients/${clientId}/documents/${docId}`,
      clientDocumentMove: (clientId, docId) => `${base}/api/clients/${clientId}/documents/${docId}/move`,
      clientDocumentsReorder: (clientId) => `${base}/api/clients/${clientId}/documents/reorder`,
      clientDocumentFolders: (clientId) => `${base}/api/clients/${clientId}/document-folders`,
      clientStandardFolders: (clientId) => `${base}/api/clients/${clientId}/document-folders/standard`,
      clientDocumentFolder: (clientId, folderId) => `${base}/api/clients/${clientId}/document-folders/${folderId}`,
      // Secure client ID/TFN upload link — staff side (authenticated).
      clientIdVerificationSend: (clientId) => `${base}/api/clients/${clientId}/id-verification/send`,
      clientIdVerificationRevoke: (clientId) => `${base}/api/clients/${clientId}/id-verification/revoke`,
      // ...and the client-facing side, which carries an upload session, not a Firebase token.
      idUploadSession: `${base}/api/id-upload/session`,
      idUploadState: `${base}/api/id-upload/state`,
      idUploadPhoto: `${base}/api/id-upload/photo`,
      idUploadTfn: `${base}/api/id-upload/tfn`,
      idUploadSkipBack: `${base}/api/id-upload/skip-back`,
      // Admin → Upload Link SMS: service number + message body.
      adminIdUploadSms: `${base}/api/admin/id-upload-sms`,
      envelopes: `${base}/api/envelopes`,
      envelopesDocusignOnly: `${base}/api/envelopes/docusign-only`,
      envelopeDocusignConsoleView: (envelopeId) => `${base}/api/envelopes/docusign/${envelopeId}/console-view`,
      clientEnvelopes: (clientId) => `${base}/api/clients/${clientId}/envelopes`,
      clientEnvelope: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}`,
      clientEnvelopeSend: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/send`,
      clientEnvelopeVoid: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/void`,
      clientEnvelopeResend: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/resend`,
      clientEnvelopeRefresh: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/refresh`,
      clientEnvelopeSaveSigned: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/save-signed`,
      clientEnvelopeGeneratePack: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/generate-pack`,
      clientEnvelopeEditView: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/edit-view`,
      clientEnvelopeConsoleView: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/console-view`,
      clientEnvelopeSendToClient: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/send-to-client`,
      clientEnvelopeUpdateClientEmail: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/update-client-email`,
      clientEnvelopeUpdateEmail: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/update-email`,
      clientEnvelopeDocuments: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/documents`,
      clientEnvelopeDocumentsReorder: (clientId, envelopeId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/documents/reorder`,
      clientEnvelopeDocumentPreview: (clientId, envelopeId, docId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/documents/${docId}/preview`,
      clientEnvelopeDocument: (clientId, envelopeId, docId) => `${base}/api/clients/${clientId}/envelopes/${envelopeId}/documents/${docId}`,
      sharedDocuments: `${base}/api/shared-documents`,
      sharedDocument: (docId) => `${base}/api/shared-documents/${docId}`,
      sharedDocumentMove: (docId) => `${base}/api/shared-documents/${docId}/move`,
      sharedDocumentsReorder: () => `${base}/api/shared-documents/reorder`,
      sharedDocumentFolders: `${base}/api/shared-document-folders`,
      sharedDocumentFolder: (folderId) => `${base}/api/shared-document-folders/${folderId}`,
      smsSend: `${base}/api/sms/send`,
      smsMedia: (clientId, messageId, mediaIndex) =>
        `${base}/api/sms/media/${encodeURIComponent(clientId)}/${encodeURIComponent(messageId)}/${encodeURIComponent(mediaIndex)}`,
      // Scheduled SMS — absolute, like everything here: a relative /api path on
      // Firebase Hosting returns index.html with a 200.
      scheduledSms: `${base}/api/scheduled-sms`,
      scheduledSmsItem: (id) => `${base}/api/scheduled-sms/${encodeURIComponent(id)}`,
      scheduledSmsBulk: `${base}/api/scheduled-sms/bulk`,
      scheduledSmsPreview: `${base}/api/scheduled-sms/preview`,
      scheduledSmsBatch: (batchId) => `${base}/api/scheduled-sms/batch/${encodeURIComponent(batchId)}`,
      adminSmsScheduling: `${base}/api/admin/sms-scheduling`,
      voiceToken: `${base}/api/voice/token`,
      voiceIncomingSession: `${base}/api/voice/incoming-session`,
      voiceActiveSession: `${base}/api/voice/active-session`,
      voiceCallFinalize: `${base}/api/voice/calls/finalize`,
      voiceCallRepairStale: `${base}/api/voice/calls/repair-stale`,
      voiceRecordingDownload: (callLogId, clientId) => {
        const params = new URLSearchParams();
        if (clientId) params.set('clientId', clientId);
        const query = params.toString();
        return `${base}/api/voice/recordings/${encodeURIComponent(callLogId)}/download${query ? `?${query}` : ''}`;
      },
      voiceRecordingStreamAccess: (callLogId, clientId) => {
        const params = new URLSearchParams();
        if (clientId) params.set('clientId', clientId);
        const query = params.toString();
        return `${base}/api/voice/recordings/${encodeURIComponent(callLogId)}/stream-access${query ? `?${query}` : ''}`;
      },
      voiceRecordingTranscript: (callLogId) =>
        `${base}/api/voice/recordings/${encodeURIComponent(callLogId)}/transcript`,
      adminCallLogs: `${base}/api/admin/call-logs`,
      adminVoiceSettings: `${base}/api/admin/voice-settings`,
      adminVoicemailGreetings: `${base}/api/admin/voicemail-greetings`,
      adminVoicemailGreeting: (number) =>
        `${base}/api/admin/voicemail-greetings/${encodeURIComponent(number)}`,
      adminVoicemailGreetingAudio: (number) =>
        `${base}/api/admin/voicemail-greetings/${encodeURIComponent(number)}/audio`,
      adminSmsNumbers: `${base}/api/admin/sms-numbers`,
      adminDocusignSettings: `${base}/api/admin/docusign-settings`,
      adminDocusignSendLog: `${base}/api/admin/docusign-send-log`,
      adminClaudeUsage: `${base}/api/admin/claude-usage`,
      adminAiPrompts: `${base}/api/admin/ai-prompts`,
      aiPromptsEffective: `${base}/api/ai-prompts/effective`,
      adminDocumentQaMeta: `${base}/api/admin/document-qa/meta`,
      adminDocumentQaRuns: `${base}/api/admin/document-qa/runs`,
      adminBanner: `${base}/api/admin/banner`,
      adminFactFindSectionOrder: `${base}/api/admin/fact-find-section-order`,
      adminSettingsInCode: `${base}/api/admin/settings-in-code`,
      adminPortalFilterDefaults: `${base}/api/admin/portal-filter-defaults`,
      portalFilterDefaults: `${base}/api/portal-filter-defaults`,
      myClientListViews: `${base}/api/me/client-list-views`,
      myClientListView: (viewId) => `${base}/api/me/client-list-views/${encodeURIComponent(viewId)}`,
      myTimeZone: `${base}/api/me/time-zone`,
      adminUserClientListViews: (email) => `${base}/api/admin/users/${encodeURIComponent(email)}/client-list-views`,
      adminUserClientListView: (email, viewId) => `${base}/api/admin/users/${encodeURIComponent(email)}/client-list-views/${encodeURIComponent(viewId)}`,
      adminCopyClientListViews: `${base}/api/admin/client-list-views/copy`,
      complianceWorkflowChecks: `${base}/api/compliance/workflow-checks`,
      reportsMarketing: `${base}/api/reports/marketing`,
      reportsParaplanning: `${base}/api/reports/paraplanning`,
      reportsAdviceApprovals: `${base}/api/reports/advice-approvals`,
      reportsImplementation: `${base}/api/reports/implementation`,
      reportsSignedDocuments: `${base}/api/reports/signed-documents`,
      reportsFsgEmails: `${base}/api/reports/fsg-emails`,
      reportsUnconverted: `${base}/api/reports/unconverted`,
      reportsUnconvertedMarkLost: `${base}/api/reports/unconverted/mark-lost`,
      adminMarketingImportLogs: `${base}/api/admin/marketing-import-logs`,
      adminMarketingImportLogDetail: (logId) =>
        `${base}/api/admin/marketing-import-logs/${encodeURIComponent(logId)}`,
      adminMarketingImportRetry: (logId) =>
        `${base}/api/admin/marketing-import-logs/${encodeURIComponent(logId)}/retry`,
      adminClientJson: (clientId) =>
        `${base}/api/admin/clients/${encodeURIComponent(clientId)}/json`,
      adminClientJsonAudit: (clientId) =>
        `${base}/api/admin/clients/${encodeURIComponent(clientId)}/json/audit`,
      rawClient: (clientId) => `${base}/api/raw-client/${encodeURIComponent(clientId)}`,
      siteBanner: `${base}/api/site-banner`,
    };
  }

  // Helper method to get environment info for debugging
  get environmentInfo() {
    return {
      hostname: this.hostname,
      protocol: this.protocol,
      port: this.port,
      isDevelopment: this.isDevelopment,
      isProduction: this.isProduction,
      backendUrl: this.backendUrl,
      frontendUrl: this.frontendUrl
    };
  }
}

export const env = new EnvironmentConfig();

// Log environment info in development
if (env.isDevelopment) {
  console.log('🌍 Environment Configuration:', env.environmentInfo);
}
